import { useParams } from '@tanstack/react-router'
import { useAtom } from 'jotai'
import { useEffect } from 'react'

import Body from '@/components/body/Body'
import { Box } from '@/components/ui/box'
import { Container } from '@/components/ui/container'
import { HEADER_HEIGHT } from '@/constants'
import useColorModeValue from '@/hooks/useColorModeValue'
import Chat from '@/pages/components/chat'
import LeftSide from '@/pages/components/documents/leftSide'
import { docsLeftSideExpandedAtom, getAllowedFileExtensions } from '@/store/documents/DocumentsAtom'
import { cleanData, createChat, getChatSettings, getConversationsList, setConversation } from '@/store/gigiChat/ChatAtom'
import { IConversationSettings } from '@/types/gigiChat/ConversationSettings'

const DocumentsContainer = () => {
    const params = useParams({ from: '/documents/$chatId' })

    const chatIdParam: string = params.chatId
    const getSettings = async (conversationId: string) => {
        const settings = (await getChatSettings(conversationId)) as IConversationSettings
        return settings
    }
    const [showSidebar] = useAtom(docsLeftSideExpandedAtom)
    useEffect(() => {
        cleanData()
        void getAllowedFileExtensions()
    }, [])

    useEffect(() => {
        const controller = new AbortController()
        const { signal } = controller
        void (async () => {
            await getConversationsList({ query: 'processing_strategy=DocAnalysis', isInitial: true, signal })
        })()

        return () => controller.abort('Another GET Conversations call was initiated')
    }, [])

    useEffect(() => {
        const controller = new AbortController()

        async function loadInitialData() {
            if (chatIdParam) {
                await getSettings(chatIdParam).then((res) => {
                    if (!controller.signal.aborted) {
                        res.processingStrategy = 'DocAnalysis'
                        setConversation(chatIdParam, res, 'DocAnalysis', controller.signal)
                    }
                })
            } else {
                await createChat('DocAnalysis', undefined, controller.signal)
            }
        }
        void loadInitialData()

        return () => {
            controller.abort('Another LOAD documents initial data call was initiated')
        }
    }, [chatIdParam])

    return (
        <Container
            maxW="100%"
            h={[`calc(100% - ${HEADER_HEIGHT.mobile})`, `calc(100% - ${HEADER_HEIGHT.mobile})`, `calc(100% - ${HEADER_HEIGHT.desktop})`]}
            display="flex"
            flexDirection="column"
            flexGrow="1"
            padding="0"
        >
            <Body>
                <Box
                    maxWidth="400px"
                    width={showSidebar ? '100%' : '30px'}
                    mt="16px"
                    mr="16px"
                    height="calc(100% - 16px)"
                    borderRadius="0 8px"
                    boxShadow="0px 0px 8px 1px rgba(84, 79, 64, 0.06)"
                    borderLeft="0"
                    backgroundColor={useColorModeValue('white', 'dark.900')}
                    display={['none', 'none', 'none', 'block']}
                >
                    <LeftSide />
                </Box>
                <Box
                    flex={2}
                    position="relative"
                    mt="16px"
                    borderRadius={showSidebar ? '8px 0px 0px' : '8px 8px 0px 0px'}
                    boxShadow="0px 0px 8px 1px rgba(84, 79, 64, 0.06)"
                    borderRight="0"
                    backgroundColor={useColorModeValue('white', 'dark.900')}
                    mx={showSidebar ? '0' : 'auto'}
                    maxW={showSidebar ? ['100%', '100%', '100%', '100%'] : ['100%', '100%', '100%', '80%']}
                >
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            bottom: 0,
                            left: 0,
                            right: 0,
                            paddingLeft: '24px',
                            paddingRight: '24px',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <Chat />
                    </div>
                </Box>
            </Body>
        </Container>
    )
}

export default DocumentsContainer
